import { ReactElement, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuard } from '@ohpen/react-authz';
import { ConfirmDeleteModal, CrudMode, Page, notification } from '@ohpen/enso-ui';

import { ImportMatchingCrud, ImportModal, ImportsList } from '@pmp/components';
import { usePmpFetch } from '@pmp/hooks';
import { ActionEntityType, ImportResult, importsDeleteImport } from '@pmp/api';
import { createDeleteErrorException, formatDateTime } from '@pmp/utils';
import { Typography } from 'antd';
import { initialState, reducer } from './reducer';

const { Text } = Typography;

export const ImportsPage = (): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoadingImports, , importsResult, , getImports] = usePmpFetch('importsGetLatestImports');
  const { allowed: canWrite } = useGuard({
    permissions: ['imports:update'],
    flags: ['enableImports'],
  });
  const { t } = useTranslation();

  useEffect(() => {
    getImports();
  }, [getImports]);

  const refreshTable = useCallback(() => {
    getImports();
  }, [getImports]);

  const showDeleteMessage = useCallback(() => {
    notification().success({ title: t('global.notifications.xDeletedSuccessfully', { x: t('global.labels.import') }) });
  }, [t]);

  const onConfirmDelete = useCallback(() => {
    return state.deleteModal?.record?.importId
      ? importsDeleteImport({ path: { importId: state.deleteModal.record.importId } })
          .then(() => {
            dispatch({ type: 'closeDeleteModal' });
            refreshTable();
            return { description: t('global.notifications.xDeletedSuccessfully', { x: t('global.labels.import') }) };
          })
          .catch(error => {
            dispatch({ type: 'closeDeleteModal' });
            return Promise.reject(createDeleteErrorException(error));
          })
      : Promise.reject(createDeleteErrorException({ title: t('global.errors.invalidRecord') }));
  }, [refreshTable, state.deleteModal.record?.importId, t]);

  const onCancelDelete = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const onCloseImportModal = useCallback(
    (skipRefreshTable?: boolean) => {
      dispatch({ type: 'closeImportModal' });
      if (skipRefreshTable) return;
      refreshTable();
    },
    [refreshTable],
  );

  const onOpenImportModal = useCallback((record: ImportResult | null, entityType: ActionEntityType, readOnly: boolean) => {
    dispatch({ type: 'openImportModal', record, entityType, readOnly });
  }, []);

  const onOpenConfig = useCallback(
    (entityType: ActionEntityType) => {
      dispatch({ type: 'openImportMatching', record: null, entityType, mode: canWrite ? CrudMode.EDIT : CrudMode.VIEW });
    },
    [canWrite],
  );

  const onCloseConfig = useCallback(() => {
    dispatch({ type: 'closeImportMatching' });
  }, []);

  const onDelete = useCallback((record: ImportResult) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  return (
    <Page>
      <Page.Title>{t('global.titles.imports')}</Page.Title>
      <Page.Content>
        <ImportsList
          isLoadingConfig={state.importMatching.open}
          isLoadingImports={isLoadingImports}
          importsResult={importsResult ?? null}
          onConfig={onOpenConfig}
          onOpenImportModal={onOpenImportModal}
          onDelete={onDelete}
        />
        {state.importModal.open && state.importModal.entityType && (
          <ImportModal
            importEntity={state.importModal.entityType}
            importId={state.importModal.importId}
            creationTime={state.importModal.creationTime}
            status={state.importModal.status}
            readOnly={state.importModal.readOnly}
            onDeleteImport={showDeleteMessage}
            onClose={onCloseImportModal}
          />
        )}
        <ConfirmDeleteModal
          title={t('global.labels.deleteX', { x: t('global.labels.import') })}
          open={state.deleteModal.open}
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          <Text>
            {t('global.labels.importDeleteConfirmationX', {
              x: formatDateTime(state.deleteModal.record?.creationTime),
            })}
          </Text>
        </ConfirmDeleteModal>
        {state.importMatching.open && (
          <ImportMatchingCrud entityType={state.importMatching.entityType} mode={state.importMatching.mode} onClose={onCloseConfig} />
        )}
      </Page.Content>
    </Page>
  );
};
